import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Column, Row, SortingRule, TableHeaderProps, useFlexLayout, useResizeColumns, UseResizeColumnsColumnOptions, UseResizeColumnsColumnProps, UseResizeColumnsState, useSortBy, UseSortByColumnOptions, UseSortByColumnProps, UseSortByOptions, UseSortByState, useTable } from 'react-table';
import { HasClassName } from '../component-utils';
import { useDebouncedCallback } from './Autocomplete';
//import { ReactComponent as DescIcon } from './icons/sort-amount-down.svg';
//import { ReactComponent as AscIcon } from './icons/sort-amount-up.svg';

export type TableColumn<T extends object> = Column<T> & UseResizeColumnsColumnOptions<T> & UseSortByColumnOptions<T> & {
  className?: string
  headerClassName?: string
}

export type SortRules<T extends object> = Required<SortingRule<T>>[]

type Props<T extends object> = HasClassName & {
  data: T[]
  columns: TableColumn<T>[]
  getRowProps?(data: T): React.HTMLAttributes<HTMLTableRowElement>
  loading?: boolean
  trComponent?: React.ReactType
  sortBy?: SortRules<T>
  onSortBy?: React.Dispatch<SortRules<T>>
  onColumnResize?: React.Dispatch<Resizing>
  maxTableHeight?: number
  onRowsUpdate?: React.Dispatch<Array<Row<T>>>
  onData?(data: T[]): void
} & ({ expandable?: false } | {
  expandable: true | ((data: T) => boolean)
  isExpanded(data: T, index: number): boolean
  onExpand(data: T, index: number): void
  children(data: T): React.ReactNode
})

const defaultColumn = {
  // When using the useFlexLayout:
  minWidth: 30, // minWidth is only used as a limit for resizing
  width: 150, // width is used for both the flex-basis and flex-grow
  maxWidth: 500, // maxWidth is only used as a limit for resizing
}

const expanderStyle: Partial<React.CSSProperties> = {
  boxSizing: 'border-box',
  flex: '0 0 auto',
  width: 35,
}

export type Resizing = {
  startX?: number;
  columnWidth: number;
  headerIdWidths: Record<string, number>;
  columnWidths: any;
  isResizingColumn?: string;
}

const emptySort: never[] = []


export function FlexTable<T extends object>({ className, data, columns, getRowProps, trComponent, sortBy, onSortBy, onColumnResize, maxTableHeight, onRowsUpdate, onData, ...props }: Props<T>) {
  const sortByOptions: UseSortByOptions<T> = {
    autoResetSortBy: false,
    disableSortRemove: true,
  }

  const sortByState: UseSortByState<T> = { sortBy: sortBy || emptySort }

  const table = useTable(
    {
      columns,
      data,
      defaultColumn,
      ...sortByOptions,
      initialState: {
        ...sortByState as any,
      },
    },
    useResizeColumns,
    useFlexLayout,
    useSortBy,
  )
  const [redraw, setRedraw] = useState<boolean>(false)
  const { getTableProps, headerGroups, rows, prepareRow, state } = table
  const { setSortBy } = table as unknown as { setSortBy: (sortBy: SortRules<T>) => void }
  const lastColumn = useRef<UseResizeColumnsState<T>>()
  const lastRowCountNotify = useRef<number>(0)
  const lastRowSortedNotify = useRef(false)


  useEffect(() => {
    if (!lastRowSortedNotify.current && onRowsUpdate && rows) {
      onRowsUpdate(rows)
      lastRowSortedNotify.current = true
    }
  }, [onRowsUpdate, rows])

  useEffect(() => {
    if (onRowsUpdate && rows && lastRowCountNotify.current !== rows.length) {
      onRowsUpdate(rows)
      lastRowCountNotify.current = rows.length
    }
  }, [onRowsUpdate, rows])

  useEffect(() => {
    if (sortBy) {
      setSortBy(sortBy)
    }
  }, [setSortBy, sortBy])

  const currentSortBy = (state as UseSortByState<T>).sortBy
  useEffect(() => {
    if (onSortBy) {
      onSortBy(currentSortBy as any)
    }
    lastRowSortedNotify.current = false
  }, [currentSortBy, onSortBy])

  useEffect(() => {
    if (onData) {
      onData(rows.map(r => r.original))
    }
  }, [onData, rows])

  const reload = useDebouncedCallback(() => {
    setRedraw(!redraw)
  }, 100);

  const currentResize = (state as UseResizeColumnsState<T>).columnResizing
  useEffect(() => {
    if (lastColumn.current === null || lastColumn.current === undefined || (lastColumn.current && lastColumn.current.columnResizing !== currentResize)) {
      onColumnResize && onColumnResize(currentResize as Resizing)

      lastColumn.current = {
        columnResizing: currentResize,
      }

      reload()
    }
  }, [currentResize, onColumnResize, redraw, reload])

  const style = maxTableHeight ? {
    maxHeight: maxTableHeight + 'px',
  } : {}

  return <div className={classNames('ReactTable', className)} style={style}>
    <div {...getTableProps()} className='rt-table'>
      <div className='rt-thead -header'>
        {headerGroups.map(headerGroup => (
          <div {...headerGroup.getHeaderGroupProps()} className='rt-tr'>
            {props.expandable && <div className='rt-th' role='columnheader' style={expanderStyle} />}
            {headerGroup.headers.map(column => {
              const columnEx = column as TableColumn<T>
              const resizableColumn = column as unknown as UseResizeColumnsColumnProps<T>
              const sortedColumn = column as unknown as UseSortByColumnProps<T>
              const sortClass = sortedColumn.isSorted
              ? sortedColumn.isSortedDesc ? '-sort-desc' : '-sort-asc'
              : null

              const resizeProps: Partial<TableHeaderProps> = resizableColumn.canResize ? {} : { style: { flex: '0 0 auto' } }

              return <div {...column.getHeaderProps([sortedColumn.getSortByToggleProps(), resizeProps])}
                className={classNames('rt-th rt-resizable-header', columnEx.headerClassName)}>
                {column.render('Header')}
                {<span className={'rt-th-order'+ sortClass}></span>}
                {resizableColumn.canResize && (
                  <div {...resizableColumn.getResizerProps({ style: { cursor: undefined } })} onClick={e => e.stopPropagation()}
                    className={classNames('rt-resizer', resizableColumn.isResizing && 'isResizing')}
                  />
                )}
              </div>
            })}
          </div>
        ))}
      </div>
      <div className='rt-tbody'>
        {rows.map((row, index) => {
          const expandable = props.expandable ? (props.expandable === true || props.expandable(row.original)) : false
          const expandRow = expandable && props.expandable ? props.onExpand : undefined

          return <TableRow key={row.id} row={row} prepareRow={prepareRow} getRowProps={getRowProps}
            index={index} onExpand={expandRow} tableExpandable={Boolean(props.expandable)}
            expanded={props.expandable && expandable ? props.isExpanded(row.original, index) : false}
            trComponent={trComponent}
            getChildren={props.expandable ? props.children : undefined} redraw={redraw} />
        })}
      </div>
    </div>
  </div >
}

type TableRowProps<T extends object> = {
  row: Row<T>
  prepareRow: (row: Row<T>) => void
  index: number
  getRowProps: Props<T>['getRowProps']
  trComponent?: React.ReactType

  tableExpandable: boolean
  expanded: boolean
  onExpand?: (data: T, index: number) => void
  getChildren?: (data: T) => React.ReactNode

  redraw: boolean
}

function TableRowFunc<T extends object>({ row, prepareRow, tableExpandable, expanded, getRowProps, index, getChildren, onExpand, trComponent, redraw }: TableRowProps<T>) {
  prepareRow(row)
  const rowProps = getRowProps ? getRowProps(row.original) : {}

  function onExpanderClick(e: React.SyntheticEvent) {
    if (!onExpand) { return }
    e.stopPropagation()
    onExpand(row.original, index)
  }

  const Tr = trComponent || 'div'

  return <div className='rt-tr-group'>
    <Tr {...row.getRowProps()} {...rowProps} className={classNames('rt-tr', rowProps.className)}>
      {tableExpandable && <div className='rt-td' style={expanderStyle}>
        {onExpand && <div onClick={onExpanderClick} className={classNames('rt-expander', expanded && '-open')} />}
      </div>}
      {row.cells.map(cell => {
        return (
          <div {...cell.getCellProps()} className={classNames('rt-td', (cell.column as TableColumn<T>).className)}>
            {cell.render('Cell', { key: 'cell' })}
          </div>
        )
      })}
    </Tr>
    {expanded && getChildren && getChildren(row.original)}
  </div>
}

const TableRow = React.memo(TableRowFunc, (prev, next) => {
  return prev.row.original === next.row.original
    && prev.prepareRow === next.prepareRow
    && prev.index === next.index
    && prev.getRowProps === next.getRowProps
    && prev.tableExpandable === next.tableExpandable
    && prev.expanded === next.expanded
    && prev.onExpand === next.onExpand
    && prev.getChildren === next.getChildren
    && prev.trComponent === next.trComponent
    && prev.row.getRowProps === next.row.getRowProps
    && prev.redraw === next.redraw
}) as typeof TableRowFunc
